import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

const RETURN_CATEGORIES = new Set(['product_return', 'partial_return']);

export const MostReturnedProductsChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.breakdown) return [];

        const productReturns: Record<string, { name: string; returns: number }> = {};

        for (const item of finance.breakdown) {
            if (item.type === 'EXPENSE' && RETURN_CATEGORIES.has(item.category as string)) {
                const id = item.entityId as string;
                if (!productReturns[id]) {
                    productReturns[id] = {
                        name: (item.entityName as string) || id,
                        returns: 0,
                    };
                }
                productReturns[id].returns += Number(item.value) || 0;
            }
        }

        return Object.values(productReturns)
            .sort((a, b) => b.returns - a.returns)
            .slice(0, 10);
    }, [finance?.breakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={120}
                            tickFormatter={(value: string) =>
                                value.length > 18 ? `${value.slice(0, 18)}...` : value
                            }
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number) => [
                                formatCurrency(value, currencySymbol),
                                t('charts.refunds', 'Refunds'),
                            ]}
                        />
                        <Bar
                            dataKey="returns"
                            name={t('charts.refunds', 'Refunds')}
                            fill={CHART_COLORS.expense}
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
